import { useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Mail, MapPin, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { useLang } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";

const INFO = [
  { icon: MapPin, label: "contact_office", value: "Fatuhada, Dili, Timor-Leste" },
  { icon: Phone, label: "contact_phone", value: "+670 7713 8966 / 3311 480" },
  { icon: Mail, label: "contact_email", value: "info@kinos.tl" },
  { icon: Mail, label: "contact_support", value: "service@kinos.tl" },
  { icon: Clock, label: "contact_hours", value: "Mon – Sat, 8:30 – 17:30" },
];

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t("contact_success"));
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const field =
    "bg-[#0B1120] border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#ED1C24] rounded-none h-12";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#0F172A] px-6 md:px-12 lg:px-24 py-20 lg:py-32"
    >
      <div className="grid lg:grid-cols-2 gap-14">
        <Reveal>
          <p className="text-xs tracking-[0.25em] uppercase text-[#ED1C24] mb-4">
            {t("contact_label")}
          </p>
          <h2 className="font-display font-semibold text-white text-4xl sm:text-5xl tracking-tight leading-tight mb-6">
            {t("contact_title1")}
            <br />
            {t("contact_title2").replace(".", "")}<span className="text-[#ED1C24]">.</span>
          </h2>
          <p className="text-slate-400 max-w-md mb-12">
            {t("contact_sub")}
          </p>
          <div className="space-y-6">
            {INFO.map((item) => (
              <div key={item.label} className="flex gap-4 items-start group">
                <span className="w-11 h-11 shrink-0 border border-white/10 flex items-center justify-center text-[#FFC61A] group-hover:border-[#ED1C24] group-hover:text-[#ED1C24] transition-colors duration-300">
                  <item.icon size={17} />
                </span>
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
                    {t(item.label)}
                  </p>
                  <p className="text-slate-200 mt-1">{item.value}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="bg-[#0B1120] border border-white/10 p-8 lg:p-10 space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Input
                data-testid="contact-name-input"
                required
                placeholder={t("contact_ph_name")}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={field}
              />
              <Input
                data-testid="contact-email-input"
                required
                type="email"
                placeholder={t("contact_ph_email")}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={field}
              />
            </div>
            <Input
              data-testid="contact-subject-input"
              placeholder={t("contact_ph_subject")}
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className={field}
            />
            <Textarea
              data-testid="contact-message-input"
              required
              placeholder={t("contact_ph_message")}
              rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="bg-[#0B1120] border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#ED1C24] rounded-none"
            />
            <button
              data-testid="contact-submit-btn"
              type="submit"
              disabled={sending}
              className="group w-full inline-flex items-center justify-center gap-3 bg-[#ED1C24] hover:bg-[#C81016] disabled:opacity-60 text-white font-medium py-4 transition-all duration-300 hover:shadow-[0_0_30px_rgba(237,28,36,0.4)]"
            >
              {sending ? t("contact_sending") : t("contact_send")}
              <Send size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <p className="text-center text-xs text-slate-500 pt-1">
              <Link data-testid="contact-support-link" to="/support" className="text-[#FFC61A] hover:text-white transition-colors duration-300 underline underline-offset-4">
                {t("contact_support_cta")} →
              </Link>
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
